import { useEffect, useState } from 'react';
import Select from 'react-select';
import { CustomProductPricingConfig, DurationUnit } from '../../../clients/quipli-service/pricing/pricing';
import { FieldCounter, FieldLabel } from '../../../components/field/field';
import { Modal } from '../../../components/modal/modal';
import { toast } from '../../../util/toasts';
import { ApiError } from '../../../clients/axios-instance';
import { Typography } from '../../../components/typography/typography';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { Button } from '../../../components/form/button';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';

export const CustomProductPricingConfigModal = ({
  isOpen,
  config,
  close,
}: {
  isOpen: boolean;
  config?: CustomProductPricingConfig;
  close: (refresh?: boolean) => void;
}) => {
  const { createCustomProductPricingConfig, updateCustomProductPricingConfig } = usePricingApiV1();

  const [isSaving, setIsSaving] = useState(false);
  const [durationUnit, setDurationUnit] = useState<DurationUnit | undefined>(config?.durationUnit);
  const [durationValue, setDurationValue] = useState<number>(config?.durationValue || 1);

  useEffect(() => {
    if (config) {
      setDurationUnit(config.durationUnit);
      setDurationValue(config.durationValue);
    } else {
      resetState();
    }
  }, [config]);

  const unitOptions = [
    { label: 'hour(s)', value: DurationUnit.hour },
    { label: 'day(s)', value: DurationUnit.day },
    { label: 'week(s)', value: DurationUnit.week },
    { label: 'month(s)', value: DurationUnit.month },
  ];

  const isSaveDisabled: boolean = !durationUnit || !durationValue;

  const resetState = () => {
    setDurationUnit(undefined);
    setDurationValue(1);
  };

  const saveConfig = () => {
    if (isSaveDisabled) {
      throw new Error('Missing required fields to save price config');
    }

    setIsSaving(true);

    const request = {
      type: OrderType.Rental,
      durationUnit: durationUnit!,
      durationValue: durationValue!,
    };

    if (config?.id) {
      updateCustomProductPricingConfig(config.id, request)
        .then(() => {
          toast('Successfully updated custom product price duration', 'success');
          resetState();
          close(true);
        })
        .catch((err: ApiError) => {
          const errMsg =
            err.status === 400 && err.message ? err.message : 'Error updating custom product price duration';
          toast(errMsg, 'error');
        })
        .finally(() => setIsSaving(false));
    } else {
      createCustomProductPricingConfig(request)
        .then(() => {
          toast('Successfully created custom product price duration', 'success');
          resetState();
          close(true);
        })
        .catch((err: ApiError) => {
          const errMsg =
            err.status === 400 && err.message ? err.message : 'Error creating custom product price duration';
          toast(errMsg, 'error');
        })
        .finally(() => setIsSaving(false));
    }
  };

  const cancel = () => {
    resetState();
    close();
  };

  return (
    <div className="rental-price-modal">
      <Modal isOpen={isOpen} title={`${config ? 'Edit' : 'Add'} Custom Product Price Duration`}>
        <div className="space-y-5 edit-rental-price-container font-sans">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <FieldLabel label="Duration" isRequired />

              <FieldCounter value={durationValue} onChange={setDurationValue} min={1} allowDecimals={false} />
            </div>

            <div className="unit-select col-span-2">
              <FieldLabel label="Unit" isRequired />
              <Select
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    height: '40px',
                    margin: '0px',
                    padding: '0px',
                    fontStyle: 'normal',
                    borderColor: 'rgb(209 213 219)',
                    zIndex: 1001,
                  }),
                }}
                className="mt-1"
                options={unitOptions}
                value={unitOptions.find((u) => u.value === durationUnit)}
                onChange={(newValue) => setDurationUnit(newValue?.value)}
              />
            </div>
          </div>
        </div>

        <div className="inset-x-0 bottom-0 mt-8 flex justify-end button-group">
          <Button className="button-secondary" onClick={cancel}>
            <Typography type="text-sm">Cancel</Typography>
          </Button>
          <Button className="button-primary" onClick={saveConfig} disabled={isSaveDisabled || isSaving}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};
